import {
  Component,
  ChangeDetectionStrategy,
  input,
  model,
  computed,
  output,
  inject,
} from '@angular/core';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { FormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SentenceCasePipe } from '../../pipes/sentence.pipe';
import { Select } from '@openng/optimus-ui/select';
import { InputText } from '@openng/optimus-ui/inputtext';
import { InputGroup } from '@openng/optimus-ui/inputgroup';
import { IconField } from '@openng/optimus-ui/iconfield';
import { InputIcon } from '@openng/optimus-ui/inputicon';

export const ALL_SEARCH_FIELDS = 'all';

export interface SearchEvent {
  searchTerm: string;
  searchField: string;
}

export interface ISearchField {
  name: string;
  displayName?: string;
}

@Component({
  selector: 'ag1-search',
  templateUrl: './ag1-search.component.html',
  providers: [HumanizePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    AngularSvgIconModule,
    SentenceCasePipe,
    Select,
    InputText,
    InputGroup,
    IconField,
    InputIcon,
  ],
})
export class Ag1SearchComponent {
  private readonly _humanizePipe = inject(HumanizePipe);

  // (optional) allow users to search by specific fields
  // if not provided, search will be done on all fields
  // ui will not show field dropdown if not provided
  readonly searchFields = input<string[] | ISearchField[]>([]);
  readonly placeholder = input('');
  readonly disabled = input<boolean>(undefined);
  readonly showAllFieldsOption = input(true);

  // The parent may set these. The user can change them after that.
  readonly searchField = model<string>(ALL_SEARCH_FIELDS);
  readonly searchTerm = model('');

  readonly searchEvent = output<SearchEvent>();

  protected readonly hasSearchFields = computed(
    () => this.searchFields()?.length > 0,
  );

  private readonly _fieldOptions = computed<Required<ISearchField>[]>(() =>
    this.searchFields().map((field: string | ISearchField) => {
      const { name, displayName }: ISearchField =
        typeof field === 'string' ? { name: field } : field;

      return {
        name,
        displayName: displayName ?? this._humanizePipe.transform(name),
      };
    }),
  );

  protected readonly selectOptions = computed<Required<ISearchField>[]>(() =>
    this.showAllFieldsOption()
      ? [
          { name: ALL_SEARCH_FIELDS, displayName: 'All fields' },
          ...this._fieldOptions(),
        ]
      : this._fieldOptions(),
  );

  protected onFieldChange(field: string): void {
    this.searchField.set(field);

    // Changing the field reruns the current search
    if (this.searchTerm()) {
      this._search();
    }
  }

  protected onTermChange(term: string): void {
    this.searchTerm.set(term);

    // Clearing the term resets the search
    if (term === '') {
      this._search();
    }
  }

  protected onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this._search();
    }
  }

  private _search(): void {
    this.searchEvent.emit({
      searchField: this.searchField(),
      searchTerm: this.searchTerm().trim(),
    });
  }
}
